import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Button from 'src/components/Button/Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import Input from 'src/components/Input/Input';
import EmailChangeIllustration from 'src/components/EmailChangeIllustration/EmailChangeIllustration';
import { ChangeEmailViewModel } from './changeEmailViewModel';
import './change-email-dialogue.scss';

interface ChangeEmailDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  viewModel: ChangeEmailViewModel;
  title: string;
  description?: string;
  note?: React.ReactNode;
  avatar?: React.ReactNode;
  maxWidth?: string;
  minHeight?: string;
  successHeading?: string;
  successBody?: React.ReactNode;
}

interface ChangeEmailFormProps {
  viewModel: ChangeEmailViewModel;
  note?: React.ReactNode;
}

const ChangeEmailForm = ({ viewModel, note }: ChangeEmailFormProps) => (
  <div className="change-email-dialogue-body">
    <Input
      label="Current email"
      value={viewModel.currentEmail}
      disabled
      block
    />

    <Input
      label="New email address"
      placeholder="name@example.com"
      value={viewModel.newEmail}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        viewModel.setNewEmail(e.target.value)
      }
      errors={viewModel.error ?? undefined}
      disabled={viewModel.isSubmitting}
      block
      required
    />

    {viewModel.confirm && (
      <Input
        label="Confirm new email address"
        placeholder="name@example.com"
        value={viewModel.confirm.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          viewModel.confirm?.onChange(e.target.value)
        }
        errors={viewModel.confirm.error ?? undefined}
        disabled={viewModel.isSubmitting}
        block
        required
      />
    )}

    {note && <p className="change-email-dialogue-note">{note}</p>}
  </div>
);

interface ChangeEmailSuccessProps {
  newEmail: string;
  heading: string;
  body?: React.ReactNode;
}

const ChangeEmailSuccess = ({
  newEmail,
  heading,
  body,
}: ChangeEmailSuccessProps) => (
  <div className="change-email-dialogue-success">
    <EmailChangeIllustration
      variant="success"
      className="change-email-dialogue-success-icon"
    />
    <div className="change-email-dialogue-success-heading">{heading}</div>
    <p className="change-email-dialogue-success-body">
      {body ?? (
        <>
          We sent a verification link to{' '}
          <span className="change-email-dialogue-success-email">{newEmail}</span>{' '}
          — click it to confirm your new address.
        </>
      )}
    </p>
    <p className="change-email-dialogue-success-sub">
      Link expires in 24 hours — check spam if you don&apos;t see it.
    </p>
  </div>
);

const ChangeEmailDialogue = ({
  isOpen,
  onClose,
  viewModel,
  title,
  description,
  note,
  avatar,
  maxWidth = '50rem',
  minHeight,
  successHeading = 'Check your inbox',
  successBody,
}: ChangeEmailDialogueProps) => {
  const handleClose = () => {
    viewModel.reset();
    onClose();
  };

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={viewModel.isSuccess ? undefined : description}
      maxWidth={maxWidth}
      minHeight={minHeight}
      avatar={avatar}
      footer={
        viewModel.isSuccess ? (
          <Button color="teal" size="small" label="Done" onClick={handleClose} />
        ) : (
          <div className="change-email-dialogue-footer">
            <Button
              color="white"
              size="small"
              label="Cancel"
              onClick={handleClose}
              disabled={viewModel.isSubmitting}
            />
            <LoadingButton
              actionText="Send verification"
              isSubmitting={viewModel.isSubmitting}
              disabled={!viewModel.canSubmit}
              onClick={() => viewModel.submit()}
            />
          </div>
        )
      }
    >
      {viewModel.isSuccess ? (
        <ChangeEmailSuccess
          newEmail={viewModel.newEmail}
          heading={successHeading}
          body={successBody}
        />
      ) : (
        <ChangeEmailForm viewModel={viewModel} note={note} />
      )}
    </GenericDialogue>
  );
};

export default ChangeEmailDialogue;
